import * as THREE from 'three';
import type { CoordTransform } from './coords';
import type { CityData } from './types';
import { getLang } from './translations';

// ── Persistence keys ──────────────────────────────────────────────────────────
export const LABEL_SCALE_KEY  = 'ar_map_city_label_scale';
export const LABEL_OFFSET_KEY = 'ar_map_city_label_offset';
export const VIS_RANGE_KEY    = 'ar_map_city_vis_range';
export const VIS_HIER_KEY     = 'ar_map_city_vis_hier';
export const SIZE_HIER_KEY    = 'ar_map_city_size_hier';

// ── Static billboard style ────────────────────────────────────────────────────
const BILLBOARD = {
    fontSize:        14,    // logical px — rendered at DPR× for crispness
    lineHeightFactor: 1.3,  // line height as multiple of fontSize
    maxInnerW:       110,   // logical px — wrap column width (excl. padding)
    bgColor:         'rgba(15, 25, 45, 0.82)',
    textColor:       '#f0f4ff',
    borderRadius:    3,     // logical px
    padX:            6,     // logical px per side
    padY:            4,     // logical px per side (top and bottom)
};

// ── Parametric place-type model ───────────────────────────────────────────────
// World height per text line at labelScale=1, dist=REF_DIST, sizeHierarchy=1
// anchored on the 'city' OSM type (largest). All other types scale by BASE_SIZE_RATIO^sizeHierarchy.
//
// Sizing is anchored on per-line height so that:
//   apparent_font_height ∝ BASE_HEIGHT_CITY × SIZE_RATIO^hier × factor / dist
// — independent of text length. Long names become taller (more lines), not wider.
const BASE_HEIGHT_CITY = 1.0;   // world units per line for city

const BASE_VIS_RATIO: Record<string, number> = {
    city: 1.000, town: 0.667, village: 0.400, hamlet: 0.240,
};
const BASE_SIZE_RATIO: Record<string, number> = {
    city: 1.000, town: 0.711, village: 0.489, hamlet: 0.333,
};
const BASE_VIS_DEFAULT  = 0.240;
const BASE_SIZE_DEFAULT = 0.333;

// Reference camera distance for the constant-screen-size formula
const REF_DIST = 50;

// Canvas line height in logical px — constant regardless of wrap count, used to
// derive a per-line world scale that isn't distorted by fixed padding (see
// updateCityLOD / buildSprite).
const LINE_HEIGHT = Math.ceil(BILLBOARD.fontSize * BILLBOARD.lineHeightFactor);

// UI filter categories → OSM place types
// 'city' OSM type is folded into 'town' at UI level; sizing ratios stay separate
const FILTER_MAP: Record<string, string[]> = {
    town:    ['city', 'town'],
    village: ['village', 'hamlet'],
};

// ── Module state ──────────────────────────────────────────────────────────────
let currentSprites: THREE.Sprite[] = [];
let _citiesVisible  = true;
let _activeSizes    = new Set<string>(['town', 'village']);

let _labelOffset   = parseFloat(localStorage.getItem(LABEL_OFFSET_KEY) ?? '0.5');
let _visRange      = parseFloat(localStorage.getItem(VIS_RANGE_KEY)    ?? '75');
let _visHierarchy  = parseFloat(localStorage.getItem(VIS_HIER_KEY)     ?? '1.0');
let _sizeHierarchy = parseFloat(localStorage.getItem(SIZE_HIER_KEY)    ?? '1.0');

// Per-line world height for a given place type at current sizeHierarchy
function computeHeightPerLine(place: string): number {
    return BASE_HEIGHT_CITY * Math.pow(BASE_SIZE_RATIO[place] ?? BASE_SIZE_DEFAULT, _sizeHierarchy);
}

function computeVisDist(place: string): number {
    return _visRange * Math.pow(BASE_VIS_RATIO[place] ?? BASE_VIS_DEFAULT, _visHierarchy);
}

// ── Word wrap ─────────────────────────────────────────────────────────────────
function wrapText(ctx: CanvasRenderingContext2D, text: string): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
        const candidate = line ? `${line} ${word}` : word;
        if (line && ctx.measureText(candidate).width > BILLBOARD.maxInnerW) {
            lines.push(line);
            line = word;
        } else {
            line = candidate;
        }
    }
    if (line) lines.push(line);
    return lines.length ? lines : [text];
}

// ── Canvas billboard texture — wrapped, tight, high-DPI ───────────────────────
function makeTexture(label: string): {
    tex: THREE.CanvasTexture;
    logW: number;
    logH: number;
    numLines: number;
} {
    const dpr    = Math.min(window.devicePixelRatio || 1, 3);
    const font   = `bold ${BILLBOARD.fontSize}px sans-serif`;
    const lineH  = LINE_HEIGHT;

    // Wrap on a scratch canvas (no DOM needed)
    const scratch = document.createElement('canvas');
    const sctx    = scratch.getContext('2d')!;
    sctx.font     = font;
    const lines   = wrapText(sctx, label);

    // Tight canvas: width = widest line, height = all lines stacked
    const maxLineW = Math.max(...lines.map(l => Math.ceil(sctx.measureText(l).width)));
    const logW = maxLineW + BILLBOARD.padX * 2;
    const logH = lines.length * lineH + BILLBOARD.padY * 2;

    const canvas  = document.createElement('canvas');
    canvas.width  = Math.round(logW * dpr);
    canvas.height = Math.round(logH * dpr);
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);

    // Rounded background
    const r = BILLBOARD.borderRadius;
    const W = logW, H = logH;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(W - r, 0);
    ctx.quadraticCurveTo(W, 0, W, r);
    ctx.lineTo(W, H - r);
    ctx.quadraticCurveTo(W, H, W - r, H);
    ctx.lineTo(r, H);
    ctx.quadraticCurveTo(0, H, 0, H - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fillStyle = BILLBOARD.bgColor;
    ctx.fill();

    // Draw each line centred vertically within its row
    ctx.font         = font;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle    = BILLBOARD.textColor;
    for (let i = 0; i < lines.length; i++) {
        const y = BILLBOARD.padY + (i + 0.5) * lineH;
        ctx.fillText(lines[i], W / 2, y);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    return { tex, logW, logH, numLines: lines.length };
}

// ── Build a single sprite ─────────────────────────────────────────────────────
function buildSprite(city: CityData, coords: CoordTransform): THREE.Sprite {
    const label = getLang() === 'cs' ? city.name : (city.name_en || city.name);
    const { tex, logW, logH } = makeTexture(label);

    const mat = new THREE.SpriteMaterial({
        map:         tex,
        transparent: true,
        depthTest:   false,   // always on top — no terrain clipping
        depthWrite:  false,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.renderOrder = 999;

    // Initial scale — overwritten each frame by updateCityLOD
    const scale  = computeHeightPerLine(city.place) / LINE_HEIGHT;
    const worldH = scale * logH;
    const worldW = scale * logW;
    sprite.scale.set(worldW, worldH, 1);

    const { x, z } = coords.geoToWorld(city.lon, city.lat);
    const exagY     = coords.sampleTerrainY(city.lon, city.lat);
    const rawY      = coords.exaggeration > 0 ? exagY / coords.exaggeration : exagY;

    sprite.position.set(x, exagY + _labelOffset, z);

    sprite.userData['baseY']         = rawY;
    sprite.userData['place']         = city.place;
    sprite.userData['uiCategory']    = placeToUiCategory(city.place);
    sprite.userData['filterVisible'] = true;
    sprite.userData['logW']          = logW;
    sprite.userData['logH']          = logH;

    return sprite;
}

function placeToUiCategory(place: string): string {
    for (const [cat, places] of Object.entries(FILTER_MAP)) {
        if (places.includes(place)) return cat;
    }
    return 'village';
}

// ── Setters ───────────────────────────────────────────────────────────────────
export function setLabelOffset(offset: number, persist = true): void {
    _labelOffset = offset;
    if (persist) localStorage.setItem(LABEL_OFFSET_KEY, String(offset));
}

export function setVisRange(v: number, persist = true): void {
    _visRange = v;
    if (persist) localStorage.setItem(VIS_RANGE_KEY, String(v));
}

export function setVisHierarchy(v: number, persist = true): void {
    _visHierarchy = v;
    if (persist) localStorage.setItem(VIS_HIER_KEY, String(v));
}

export function setSizeHierarchy(v: number, persist = true): void {
    _sizeHierarchy = v;
    if (persist) localStorage.setItem(SIZE_HIER_KEY, String(v));
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function loadAndPlaceCities(
    slug: string,
    coords: CoordTransform,
    scene: THREE.Object3D,
): Promise<void> {
    clearCityLabels(scene);

    let cities: CityData[];
    try {
        const v   = coords.meta.citiesVersion ? `?v=${coords.meta.citiesVersion}` : '';
        const res = await fetch(`/data/${slug}/cities.json${v}`);
        if (!res.ok) return;
        cities = await res.json() as CityData[];
    } catch {
        return;
    }

    for (const city of cities) {
        const sprite = buildSprite(city, coords);
        scene.add(sprite);   // works for Scene or Group (both extend Object3D)
        currentSprites.push(sprite);
    }

    applyFilter();
}

export function clearCityLabels(scene: THREE.Object3D): void {
    for (const sprite of currentSprites) {
        scene.remove(sprite);
        (sprite.material as THREE.SpriteMaterial).map?.dispose();
        sprite.material.dispose();
    }
    currentSprites = [];
}

export function rescaleCityLabels(exaggeration: number): void {
    for (const sprite of currentSprites) {
        const rawY = sprite.userData['baseY'] as number;
        sprite.position.y = rawY * exaggeration + _labelOffset;
    }
}

/** Per-frame: distance cull + scale so apparent text height is city-type-controlled.
 *
 *  scale (world units per canvas px) = computeHeightPerLine(place) × distFactor × labelScale / LINE_HEIGHT
 *  world-height = scale × logH
 *  world-width  = scale × logW
 *
 *  Deriving scale from LINE_HEIGHT (constant) rather than logH (which includes
 *  fixed padding) keeps apparent glyph size independent of how many lines the
 *  text wraps to.
 */
export function updateCityLOD(
    camera: THREE.Camera,
    labelScale: number,
    worldRoot?: THREE.Object3D,
): void {
    // When worldRoot is scaled (AR mode), compute camera position in local space
    // so distance-based sizing uses world-unit distances, not real-world metres.
    const camLocal = worldRoot
        ? worldRoot.worldToLocal(camera.position.clone())
        : camera.position;

    for (const sprite of currentSprites) {
        if (!(sprite.userData['filterVisible'] as boolean)) {
            sprite.visible = false;
            continue;
        }

        const place   = sprite.userData['place'] as string;
        const dist    = camLocal.distanceTo(sprite.position);
        const maxDist = computeVisDist(place);

        if (dist > maxDist) {
            sprite.visible = false;
            continue;
        }
        sprite.visible = true;

        const factor   = Math.max(0.4, Math.min(3.0, dist / REF_DIST));
        const logW     = sprite.userData['logW'] as number;
        const logH     = sprite.userData['logH'] as number;
        const scale    = computeHeightPerLine(place) * factor * labelScale / LINE_HEIGHT;
        const worldH   = scale * logH;
        const worldW   = scale * logW;
        sprite.scale.set(worldW, worldH, 1);
    }
}

export function setCitiesVisible(visible: boolean): void {
    _citiesVisible = visible;
    applyFilter();
}

export function toggleCitySize(category: string): boolean {
    if (_activeSizes.has(category)) {
        _activeSizes.delete(category);
    } else {
        _activeSizes.add(category);
    }
    applyFilter();
    return _activeSizes.has(category);
}

function applyFilter(): void {
    for (const sprite of currentSprites) {
        const cat = sprite.userData['uiCategory'] as string;
        const fv  = _citiesVisible && _activeSizes.has(cat);
        sprite.userData['filterVisible'] = fv;
        if (!fv) sprite.visible = false;
    }
}
